/* eslint-disable max-lines */
import React from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import { media } from '@this/components/shared/atoms/media';
import PdfIcon from '@material-ui/icons/PictureAsPdf';
import A from '@this/components/shared/atoms/a';
import Text from '@this/components/shared/text/text';
import TranslateIgnoreText from '@this/components/shared/text/translate_ignore_text';
import HotelElement from '@this/domain/hotel_element';
import type Trip from '@this/domain/trip/trip';
import type NonOrderItem from '@this/domain/non_order_item';
import MarketLogHelper from '@this/domain/market_log/market_log_helper';
import { toDomesticTel, digits, isAiTravel } from '@this/src/util';
import type TransportElementAttachFile from '@this/domain/transport_element_attach_file';
import type ElementAttachFile from '@this/domain/element_attach_file';
import Box from '@material-ui/core/Box';
import OrderItemStatusLabelTemplate from './order_item_status_label.template';
import GoogleCalendarAddButton from './calendar/google_calendar_add_button';
import OutlookCalendarAddButton from './calendar/outlook_calendar_add_button';
import type { Translatedline, Theme } from './trip_calendar_helpers';
import TripCardHotelTypeLine from './trip_card_hotel_type_line';

interface Props {
  trip: any;
  user: any;
  theme: Theme;
  serviceId: number;
  simpleRequestType: boolean;
  hideFooter?: boolean;
}

const TripCardLeft: React.FC<Props> = ({ trip, user, theme, serviceId, simpleRequestType, hideFooter }) => {
  const travelerNames: string[] = trip.travelerNames();

  const sortTransportElementAttachFiles = (
    files: TransportElementAttachFile[],
    travelerInformations: any[],
    userId: number
  ): TransportElementAttachFile[] => {
    return files.slice().sort((a, b) => {
      const aUserIdMatch = travelerInformations.some(
        traveler => traveler.id === a.travelerInformationId && traveler.userId === userId
      );
      const bUserIdMatch = travelerInformations.some(
        traveler => traveler.id === b.travelerInformationId && traveler.userId === userId
      );
      if (aUserIdMatch && !bUserIdMatch) return -1;
      if (!aUserIdMatch && bUserIdMatch) return 1;
      return a.id - b.id;
    });
  };

  return trip.draft ? (
    <BlockContent>
      <Box width="auto" margin="0 0 20px" border="none" style={{ verticalAlign: 'top' }}>
        <tbody>
          {trip.title ? (
            <tr>
              <MainTdData>旅程名</MainTdData>
              <MainTd>
                <TranslateIgnoreText text={trip.title} />
              </MainTd>
            </tr>
          ) : (
            <tr />
          )}
          {trip.in_advance_approval ? (
            <tr>
              <MainTdData>申請者</MainTdData>
              <MainTd>
                <TranslateIgnoreText text={trip.user.name} />
              </MainTd>
            </tr>
          ) : (
            <tr />
          )}
          {travelerNames &&
            travelerNames.map((tn, i) => (
              <tr key={i}>
                {travelerNames.length > 1 ? (
                  <MainTdData>{`出張者${i + 1}`}</MainTdData>
                ) : (
                  <MainTdData>出張者</MainTdData>
                )}
                <MainTd>
                  <TranslateIgnoreText text={tn} />
                </MainTd>
              </tr>
            ))}
          <tr>
            <MainTdData>期間</MainTdData>
            <MainTd>
              <TranslateIgnoreText text={trip.dateRange()} />
            </MainTd>
          </tr>
          {trip.final_destination ? (
            <tr>
              <MainTdData>訪問先</MainTdData>
              <MainTd>
                <TranslateIgnoreText text={trip.final_destination} />
              </MainTd>
            </tr>
          ) : (
            <tr />
          )}
          {trip.purpose ? (
            <tr>
              <MainTdData>目的</MainTdData>
              <MainTd>
                <TranslateIgnoreText text={trip.purpose} />
              </MainTd>
            </tr>
          ) : (
            <tr />
          )}
        </tbody>
      </Box>
    </BlockContent>
  ) : (
    <>
      {trip.order.orderItems.map((orderItem: any, i: number) => (
        <MainContent key={`${i}`}>
          <MainContentBody>
            {!trip.draft &&
              trip.currentApprovalStatus() !== 0 &&
              ![0, 8, 9, 10, 11, 12].includes(trip.status) &&
              _.isEmpty(trip.isApproveCanceled()) &&
              trip.currentApprovalStatus() !== 2 && (
                <HeadStatus>
                  <OrderItemStatusLabelTemplate orderItem={orderItem} />
                </HeadStatus>
              )}
            {orderItem.orderItemCategory === 'arrangement_request' ? (
              <MainBlock>
                <BlockTitle>リクエストフォーム利用料</BlockTitle>
                <BlockContent>
                  <span data-wovn-ignore>{digits(orderItem.totalPriceWithAll())}</span>
                  <span>円</span>
                </BlockContent>
              </MainBlock>
            ) : (
              <>
                {orderItem.elements.map((element: any, j: number) => (
                  <MainBlock key={`${i}_${j}`}>
                    {!orderItem.isFollowingElement(element) && <BlockTitle>{element.title()}</BlockTitle>}
                    <BlockContent>
                      {element.type === 'hotel' &&
                        (element.isCanceled(orderItem.orderItemCategory)
                          ? element.name && (
                              <>
                                <CanceledBlockTextL data-wovn-ignore>{element.name}</CanceledBlockTextL>
                                <CancelMessage>(キャンセル済み)</CancelMessage>
                              </>
                            )
                          : element.name && <BlockTextL data-wovn-ignore>{element.name}</BlockTextL>)}
                      {element.type === 'transport' && (element.name || '')}
                      <BlockTextM>
                        <TranslateIgnoreText text={element.summary()} />
                      </BlockTextM>
                      <BlockTextM>
                        {element.type === 'hotel' ? (
                          <>
                            <p>
                              返金：
                              {element.refundable === 'true' ? (
                                <span>可</span>
                              ) : element.refundable === 'false' ? (
                                <span>不可</span>
                              ) : (
                                <span>お問い合わせください</span>
                              )}
                            </p>
                            {element.structuredHotelTypeDetail().map((l: Translatedline) => {
                              return <TripCardHotelTypeLine key={l.label} line={l} />;
                            })}
                            {element.detailPath && (
                              <BlockTextM>
                                <span>宿泊詳細情報：</span>
                                <span>
                                  <a href={element.detailPath} target="_blank" rel="noopener noreferrer">
                                    詳細はコチラ
                                  </a>
                                </span>
                              </BlockTextM>
                            )}
                            <p>
                              住所:{' '}
                              <a
                                data-wovn-ignore
                                href={element.hotelMapAddressLink()}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {element.address ? element.address : '所在地を確認'}
                              </a>
                            </p>
                            <p>
                              {element.tel && (
                                <>
                                  <span>電話番号：</span>
                                  <span data-wovn-ignore>{toDomesticTel(element.tel)}</span>
                                </>
                              )}
                            </p>
                            <Room>
                              {element.structuredRoomTypeDetail().map((l: Translatedline, k: number) => {
                                return <TripCardHotelTypeLine key={`t_${k}`} line={l} />;
                              })}
                            </Room>
                            {/* ホテル予約情報 */}
                            {element.hotelReservationNumber && (
                              <>
                                <span>ホテル予約番号：</span>
                                <span data-wovn-ignore>{element.hotelReservationNumber}</span>
                              </>
                            )}
                          </>
                        ) : element.type === 'transport' ? (
                          <>
                            {/* 予約情報 */}
                            {element.ticketNumber && (
                              <BlockTextM>
                                <span>航空券番号：</span>
                                <span data-wovn-ignore>{element.ticketNumber}</span>
                              </BlockTextM>
                            )}
                            {element.reservationNumber && (
                              <BlockTextM>
                                <span>予約番号：</span>
                                <span data-wovn-ignore>{element.reservationNumber}</span>
                              </BlockTextM>
                            )}
                            {element.exicReceiptNumber && (
                              <BlockTextM>
                                <span>EXICお預かり番号：</span>
                                <span data-wovn-ignore>{element.exicReceiptNumber}</span>
                              </BlockTextM>
                            )}
                            {!orderItem.isPrecedingElement(element) && (
                              <>
                                {element.structuredDetail(orderItem).map((l: Translatedline, k: number) => {
                                  return l.translate ? (
                                    <BlockTextM key={`t_${k}`}>
                                      <span>{l.label}</span>
                                      <span>{l.value}</span>
                                    </BlockTextM>
                                  ) : (
                                    <BlockTextM key={`nt_${k}`}>
                                      <span>{l.label}</span>
                                      <span data-wovn-ignore>{l.value}</span>
                                    </BlockTextM>
                                  );
                                })}
                                <Box
                                  width="auto"
                                  margin="0 !important"
                                  border="none"
                                  style={{ verticalAlign: 'top' }}
                                >
                                  <tbody>
                                    {orderItem.travelerShinkansenTypes() &&
                                      orderItem.travelerShinkansenTypes().map((tst: string, i: number) => (
                                        <tr key={i}>
                                          {orderItem.travelerShinkansenTypes().length > 1 ? (
                                            <MainTdData>{`${i + 1}人目`}</MainTdData>
                                          ) : (
                                            <MainTdData>出張者</MainTdData>
                                          )}
                                          <MainTd>
                                            <TranslateIgnoreText text={tst} />
                                          </MainTd>
                                        </tr>
                                      ))}
                                    {orderItem.travelerAirlineTypes() &&
                                      orderItem.travelerAirlineTypes().map((tat: string, i: number) => (
                                        <tr key={i}>
                                          {orderItem.travelerAirlineTypes().length > 1 ? (
                                            <MainTdData>{`${i + 1}人目`}</MainTdData>
                                          ) : (
                                            <MainTdData>出張者</MainTdData>
                                          )}
                                          <MainTd>
                                            <TranslateIgnoreText text={tat} />
                                          </MainTd>
                                        </tr>
                                      ))}
                                  </tbody>
                                </Box>
                                {element.detailPath && (
                                  <BlockTextM>
                                    <span>宿泊詳細情報：</span>
                                    <span>
                                      <a href={element.detailPath} target="_blank" rel="noreferrer noopener">
                                        詳細はコチラ
                                      </a>
                                    </span>
                                  </BlockTextM>
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          element.type === 'wifi' && (
                            <>
                              {' '}
                              {element.structuredDetail(orderItem).map((l: Translatedline, k: number) => {
                                return <TripCardHotelTypeLine key={`t_${k}`} line={l} />;
                              })}
                              <Box
                                width="auto"
                                margin="0 !important"
                                border="none"
                                style={{ verticalAlign: 'top' }}
                              >
                                <tbody>
                                  {orderItem.travelerShinkansenTypes() &&
                                    orderItem.travelerShinkansenTypes().map((tst: string, i: number) => (
                                      <tr key={i}>
                                        {orderItem.travelerShinkansenTypes().length > 1 ? (
                                          <MainTdData>{`${i + 1}人目`}</MainTdData>
                                        ) : (
                                          <MainTdData>出張者</MainTdData>
                                        )}
                                        <MainTd>
                                          <TranslateIgnoreText text={tst} />
                                        </MainTd>
                                      </tr>
                                    ))}
                                  {orderItem.travelerAirlineTypes() &&
                                    orderItem.travelerAirlineTypes().map((tat: string, i: number) => (
                                      <tr key={i}>
                                        {orderItem.travelerAirlineTypes().length > 1 ? (
                                          <MainTdData>{`${i + 1}人目`}</MainTdData>
                                        ) : (
                                          <MainTdData>出張者</MainTdData>
                                        )}
                                        <MainTd>
                                          <TranslateIgnoreText text={tat} />
                                        </MainTd>
                                      </tr>
                                    ))}
                                </tbody>
                              </Box>
                              {element.reservationNumber && (
                                <BlockTextM>
                                  <span>注文番号：</span>
                                  <span data-wovn-ignore>{element.reservationNumber}</span>
                                </BlockTextM>
                              )}
                            </>
                          )
                        )}
                        {!orderItem.isPrecedingElement(element) && (
                          <>
                            <Space />
                            {element.type === 'transport' && (
                              <>
                                {theme.themeClass === 'tabikobo' && orderItem.price.flightPriceChangeDetail && (
                                  <p>{orderItem.price.flightPriceChangeDetail}</p>
                                )}
                                {element.isRawTicketingExpired(orderItem) && (
                                  <p>
                                    発券期限：
                                    <Deadline>{element.rawTicketingExpiredAt(orderItem)}</Deadline>
                                  </p>
                                )}
                              </>
                            )}
                            {MarketLogHelper.isElementMarketLogLinkAvailable(
                              element,
                              theme.serviceName,
                              trip as Trip
                            ) && (
                              <A
                                href={MarketLogHelper.elementMarketLogPath(element, trip.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                マーケットログを確認
                                <img
                                  className="organization-trips-detail__detail-panel__item-block__market-log-link-confirm"
                                  src="/images/external_link_icon.png"
                                  alt="link"
                                />
                              </A>
                            )}
                          </>
                        )}
                      </BlockTextM>
                      <AttachFilesContainer>
                        {sortTransportElementAttachFiles(
                          element.transportElementAttachFiles || [],
                          orderItem.travelerInformations,
                          user.id
                        ).map((transportElementAttachFile: TransportElementAttachFile) => {
                          const attachFileTraveler = orderItem.travelerInformations.find(
                            (traveler: any) => traveler.id === transportElementAttachFile.travelerInformationId
                          );
                          return (
                            <div key={transportElementAttachFile.id} style={{ flex: '0 1 auto' }}>
                              <BlockTextM style={{ display: 'flex' }}>
                                <PdfIcon style={{ color: '#a72c09', marginRight: '2px' }} />
                                <A
                                  href={`/trips/${transportElementAttachFile.tripId}/transport_element_attach_files/${transportElementAttachFile.id}.pdf`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ display: 'flex', alignItems: 'center' }}
                                >
                                  {transportElementAttachFile.uploadFileName}
                                </A>
                              </BlockTextM>
                              <AttachFileTraveler>
                                {attachFileTraveler && ` ${attachFileTraveler.name} 様`}
                              </AttachFileTraveler>
                            </div>
                          );
                        })}
                      </AttachFilesContainer>
                      {element.type === 'wifi' && element.attachFiles?.length > 0 && (
                        <AttachFilesContainer>
                          {element.attachFiles.map((file: ElementAttachFile) => (
                            <div key={file.id} style={{ flex: '0 1 auto' }}>
                              <BlockTextM style={{ display: 'flex' }}>
                                <PdfIcon style={{ color: '#a72c09', marginRight: '2px' }} />
                                <A
                                  href={`/trips/${file.tripId}/element_attach_files/${file.id}.pdf`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ display: 'flex', alignItems: 'center' }}
                                >
                                  {file.uploadFileName}
                                </A>
                              </BlockTextM>
                              <AttachFileTraveler>
                                {file.travelerInformationId &&
                                  (() => {
                                    const traveler = orderItem.travelerInformations.find(
                                      (t: any) => t.id === file.travelerInformationId
                                    );
                                    return traveler ? `${traveler.name} 様` : '';
                                  })()}
                              </AttachFileTraveler>
                            </div>
                          ))}
                        </AttachFilesContainer>
                      )}
                      {isAiTravel(serviceId) &&
                        (orderItem.ticketingMethod === 'qr_east' || orderItem.ticketingMethod === 'qr_west') && (
                          <BlockTextM>
                            {orderItem.ticketingMethod === 'qr_east' ? (
                              <A
                                href="https://aitravel.my.salesforce.com/sfc/p/#7F000002CrgE/a/TL000001LMUo/pwjAtO_q8Pn6wBGvZenzhYrsVGKLyVqbayim29VPb_Q"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                ※JRチケット発券マニュアル
                              </A>
                            ) : (
                              <A
                                href="https://aitravel.my.salesforce.com/sfc/p/#7F000002CrgE/a/TL000001LNIn/LHHz9yTUs8Oa9bz5k7ywE7EiAhoBqjuNDUJitIYaq80"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                ※JRチケット発券マニュアル
                              </A>
                            )}
                          </BlockTextM>
                        )}
                    </BlockContent>
                  </MainBlock>
                ))}
                {/* JRパッケージ 予約情報 */}
                {orderItem.orderItemCategory === 'jr_package' && (
                  <>
                    {orderItem.jrAuthCode && (
                      <BlockTextM>
                        <span>JR認証番号：</span>
                        <span data-wovn-ignore>{orderItem.jrAuthCode}</span>
                      </BlockTextM>
                    )}
                    {orderItem.jrPickupCode && (
                      <BlockTextM>
                        <span>JR引取番号：</span>
                        <span data-wovn-ignore>{orderItem.jrPickupCode}</span>
                      </BlockTextM>
                    )}
                  </>
                )}
                {orderItem.eTickets.length > 0 &&
                  orderItem.eTickets.map((ticket: any, k: number) => (
                    <p key={k}>
                      <a href={ticket.link} target="_blank" rel="noopener noreferrer">
                        e-ticketを表示
                        <img
                          className="organization-trips-detail__detail-panel__item-block__market-log-link-confirm"
                          src="/images/external_link_icon.png"
                          alt="link"
                        />
                      </a>
                    </p>
                  ))}
                {orderItem.price && (
                  <MainBlock>
                    <span data-wovn-ignore>{utils.digits(orderItem.price.totalPrice())}</span>
                    <span>円</span>
                  </MainBlock>
                )}
              </>
            )}
          </MainContentBody>
          {!hideFooter && (
            <MainContentFooter>
              <GoogleCalendarAddButton trip={trip} user={user} theme={theme} orderItem={orderItem} />
              <OutlookCalendarAddButton trip={trip} user={user} theme={theme} orderItem={orderItem} />
            </MainContentFooter>
          )}
        </MainContent>
      ))}
      {!_.isEmpty(_.get(trip, 'nonOrderItems')) && (
        <MainContent>
          <MainContentBody>
            <BlockTextL>経費予定分</BlockTextL>
            {trip.nonOrderItems.map((item: NonOrderItem, idxNonOrder: number) => (
              <MainBlock key={idxNonOrder}>
                <BlockTitle>経路{idxNonOrder + 1}</BlockTitle>
                {item.elements.map((element: any, j: number) => (
                  <BlockFlex key={j}>
                    <BlockFlexLabel>
                      【{element.transportType !== 'other' ? element.title() : '徒歩'}】
                    </BlockFlexLabel>
                    <BlockContent>
                      <BlockTextM>
                        <Text text={element.summary()} />
                      </BlockTextM>
                      {typeof element.detail === 'function' && (
                        <BlockTextM>
                          {element instanceof HotelElement ? (
                            <>
                              <Text data-wovn-ignore text={element.hotelTypeDetail()} />
                              {element.detailPath && (
                                <div data-wovn-ignore>
                                  <span>宿泊詳細情報：</span>
                                  <span>
                                    <a href={element.detailPath} target="_blank" rel="noreferrer noopener">
                                      詳細はコチラ
                                    </a>
                                  </span>
                                </div>
                              )}
                              <p data-wovn-ignore>
                                住所:{' '}
                                <a href={element.hotelMapAddressLink()} target="_blank" rel="noopener noreferrer">
                                  {element.address ? element.address : '所在地を確認'}
                                </a>
                              </p>
                              <p data-wovn-ignore>{element.telDescription()}</p>
                              <Text data-wovn-ignore text={element.roomTypeDetail()} />
                            </>
                          ) : (
                            <Text data-wovn-ignore text={element.detail()} />
                          )}
                          {simpleRequestType ? (
                            <Text
                              data-wovn-ignore
                              text={`【${item.tripTypeText}】${digits(item.price.totalPrice())}円`}
                            />
                          ) : (
                            <Text data-wovn-ignore text={`${digits(item.price.totalPrice())}円`} />
                          )}
                        </BlockTextM>
                      )}
                    </BlockContent>
                  </BlockFlex>
                ))}
                {!simpleRequestType && (
                  <>
                    <BlockTextM>
                      <Text text="※表記は現地時刻です" />
                    </BlockTextM>
                    <BlockTextM>
                      <Text data-wovn-ignore text={`${digits(item.price.totalPrice())}円`} />
                    </BlockTextM>
                  </>
                )}
              </MainBlock>
            ))}
          </MainContentBody>
        </MainContent>
      )}
    </>
  );
};

const BlockContent = styled.div`
  line-height: 1.8;
`;

const MainTd = styled.td`
  padding: 5px 0;
  border: none;
  vertical-align: top;
  color: ${props => props.theme.grayTextColor}
  font-weight: bold;

  ${media.sp`
    padding: 3px 0;
  `}
`;

const MainTdData = styled(MainTd)`
  width: 60px;
  color: ${props => props.theme.disabledColor};
`;

const MainBlock = styled.div``;

const MainContent = styled.div`
  border: solid 1px ${props => props.theme.grayBorderColor};
  background-color: #ffffff;
  margin-bottom: 12px;
  position: relative;
`;

const MainContentBody = styled.div`
  padding: 12px;
`;

const MainContentFooter = styled.div`
  padding: 12px;
  border-top: solid 1px ${props => props.theme.grayBorderColor};
  text-align: center;
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  ${media.sp`
    justify-content: center;
  `}
`;

const BlockFlex = styled.div`
  display: flex;
  gap: 8px;
`;

const BlockFlexLabel = styled.div`
  min-width: 70px;
`;

const BlockTitle = styled.p`
  font-size: 14px;
  font-weight: bold;
  line-height: 1.6;
  margin-bottom: 10px;

  ${media.sp`
    font-size: 12px;
  `}
`;

const BlockTextL = styled.div`
  font-size: 14px;
  font-weight: bold;
  ${media.sp`
    font-size: 12px;
  `}
`;

const CanceledBlockTextL = styled.div`
  font-size: 14px;
  font-weight: bold;
  text-decoration: line-through;
  ${media.sp`
    font-size: 12px;
  `}
`;

const CancelMessage = styled.div`
  color: red;
`;

const BlockTextM = styled.div``;

const Space = styled.div`
  margin-bottom: 10px;
`;

const Room = styled.div`
  margin-top: 20px;
`;

const Deadline = styled.span`
  color: ${props => props.theme.redColor};
`;

const HeadStatus = styled.div`
  padding: 8px 14px;
  background: ${props => props.theme.grayBgColor};
  border-radius: 14px;
  font-size: 12px;
  line-height: 1;
  font-weight: bold;
  text-align: center;
  width: 130px;
  position: absolute;
  right: 12px;
  top: 12px;
`;

const AttachFilesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 5px;
  justify-content: flex-start;
  align-items: flex-start;
`;

const AttachFileTraveler = styled.p`
  font-size: 10px;
  margin-left: 5px;
`;

export default TripCardLeft;
